import { call, select, takeLeading } from 'redux-saga/effects';
import {
  GlobalActions,
  GlobalPut,
  ModalType,
  NavigationRoute,
  QuoteDbInitPayload,
} from '@ps20-frontend/shop-integration-redux';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { App, isAppTypeInList } from '@ps20-frontend/frontend-models';
import { UwProductEntry } from '@ps20-frontend/uw';
import { Dialogschritt } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { selectShouldShowUwRejectionWarning } from '../selectors/uw.selector';
import {
  hasUnderwriterInfo,
  selectAllUwDecisionsComplete,
  selectHasUncheckedUwDecisions,
} from '../selectors/warenkorb.selector';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { hasUncalculatedOffer } from '../selectors/tiergruppeWarenkorb.selector';
import { selectCheckoutBlockReason } from '../selectors/checkoutBlock.selector';

function toUwProductEntry(warenkorb: WarenkorbAngebot): UwProductEntry {
  return {
    appId: warenkorb.appId,
    angebotsId: warenkorb.angebotsId,
    uw: warenkorb.uw ?? {
      shared: {
        measures: [],
        documentation: '',
        underwriterName: '',
        underwriterBensl: '',
        risikoAddress: '',
        produktName: '',
      },
      entries: [],
    },
  };
}

export function* watchNavigateToCheckoutRequested() {
  yield takeLeading(
    GlobalActions.NAVIGATE_TO_CHECKOUT_REQUESTED,
    safeSaga(navigateToCheckoutSaga, App.SHOP_DASHBOARD),
  );
}

export function* navigateToCheckoutSaga() {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  const globalActions = new GlobalActions();

  if (hasUncalculatedOffer(state.warenkorb) || selectCheckoutBlockReason(state) !== null) {
    yield call(
      [globalPut, globalPut.put],
      globalActions.requestModal(ModalType.AUSGEWAEHLTES_PRODUKT_NICHT_TARIFIERT_ERROR),
    );
    return;
  }

  if (state.vorvertraege) {
    yield call(
      [globalPut, globalPut.put],
      globalActions.updateSofortschutzEndDate(state.vorvertraege),
    );
  }

  const quoteDbInitPayload: QuoteDbInitPayload = {
    dialogschritt: Dialogschritt.DASHBOARD_JETZT_BEANTRAGEN,
  };
  yield call([globalPut, globalPut.put], globalActions.quoteDBInit(quoteDbInitPayload));

  const uwCapableWarenkorb = state.warenkorb
    .filter(warenkorb => isAppTypeInList(warenkorb.appId, [App.VWG, App.VHV]));

  const hasUwRequirement = state.warenkorb
    .filter(warenkorb => !warenkorb.beibehalten)
    .some(hasUnderwriterInfo);

  const disableSubmission =
    (hasUwRequirement && !selectAllUwDecisionsComplete(state)) ||
    selectShouldShowUwRejectionWarning(state) ||
    selectHasUncheckedUwDecisions(state);

  yield call(
    [globalPut, globalPut.put],
    globalActions.elementarFragebogenProvided({
      disableSubmission,
      products: uwCapableWarenkorb.map(toUwProductEntry),
    }),
  );

  const navigationRequest: NavigationRoute = { appId: App.SHOP_CHECKOUT };
  yield call(
    [globalPut, globalPut.put],
    globalActions.navigationRequested(navigationRequest),
  );
}
